/**
 * Cart "Subscribe & Save" upsell — swap handler.
 * --------------------------------------------------------------------------
 * The widget itself (snippets/cart-subscription-upsell.liquid) is fully
 * server-rendered per one-time line, including the matched interval, %, and
 * $ saved. This file only handles the CTA click: swap the line to the offered
 * subscription plan (+ adjusted quantity) via /cart/change.js and re-render the
 * cart/drawer in place — no page reload.
 *
 * Reuses the theme's own cart plumbing so behavior stays consistent with
 * quantity edits (assets/cart.js): the same <cart-drawer-items>/<cart-items>
 * element renders the returned sections and reconciles the free-gift line.
 *
 * A single delegated listener on `document` survives every drawer re-render
 * (the drawer's inner HTML is replaced wholesale on each update), so nothing
 * needs re-binding.
 */
(function () {
  "use strict";

  var IN_FLIGHT = "is-loading";

  function cartItemsEl() {
    return (
      document.querySelector("cart-drawer-items") ||
      document.querySelector("cart-items")
    );
  }

  // Existing line properties + our attribution stamp. Kaching's own bundle
  // property is dropped — the line is leaving the one-time bundle state, and
  // carrying it onto a subscription line could make the bundle app re-grab it.
  //
  // Shopify renders `line_item.properties | json` as an ARRAY of [key, value]
  // pairs (e.g. [["_source","cart-upsell"]]), NOT a plain object. Treating that
  // array as an object produced a broken property (numeric key 0 whose value
  // was the pair joined into "_sourcecart-upsell"). We normalise both shapes.
  function buildProperties(rawJson, sourceValue) {
    var raw = {};
    try {
      var parsed = JSON.parse(rawJson || "{}");
      if (Array.isArray(parsed)) {
        parsed.forEach(function (pair) {
          if (Array.isArray(pair) && pair.length >= 2) raw[pair[0]] = pair[1];
        });
      } else if (parsed && typeof parsed === "object") {
        Object.keys(parsed).forEach(function (key) {
          raw[key] = parsed[key];
        });
      }
    } catch (e) {
      /* no existing props */
    }

    var props = {};
    Object.keys(raw).forEach(function (key) {
      if (key.indexOf("__kaching") === 0) return;
      props[key] = raw[key];
    });
    // Distinct key so we never clobber a recommended-products "_source" already
    // on the line — a product added via that upsell and then switched to a
    // subscription here keeps BOTH attributions. The Flow matches on the value
    // (key-agnostic), so this still tags correctly.
    if (sourceValue) props._upsell_source = sourceValue;
    return props;
  }

  function swap(button) {
    if (button.classList.contains(IN_FLIGHT)) return;

    var line = parseInt(button.getAttribute("data-line"), 10);
    var planId = button.getAttribute("data-plan-id");
    var quantity = parseInt(button.getAttribute("data-quantity"), 10);
    var sourceValue = button.getAttribute("data-source");
    if (!line || !planId || !quantity) return;

    var itemsEl = cartItemsEl();
    if (!itemsEl || typeof itemsEl.getSectionsToRender !== "function") return;

    button.classList.add(IN_FLIGHT);
    button.setAttribute("aria-disabled", "true");

    var sections = itemsEl
      .getSectionsToRender()
      .map(function (section) {
        return section.section;
      });

    var body = JSON.stringify({
      line: line,
      quantity: quantity,
      selling_plan: planId,
      properties: buildProperties(button.getAttribute("data-props"), sourceValue),
      sections: sections,
      sections_url: window.location.pathname,
    });

    fetch(window.routes.cart_change_url, Object.assign({}, fetchConfig(), { body: body }))
      .then(function (response) {
        return response.json();
      })
      .then(function (parsedState) {
        if (parsedState.errors || parsedState.status) {
          console.error("[sub-upsell] swap failed:", parsedState);
          button.classList.remove(IN_FLIGHT);
          button.removeAttribute("aria-disabled");
          return;
        }

        itemsEl.renderSections(parsedState);

        // Notify the rest of the theme. Source "cart-items" is deliberately
        // NOT used here (cart.js ignores its own source), so free-gift
        // reconciliation is kicked off explicitly below instead.
        if (window.publish && window.PUB_SUB_EVENTS) {
          window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
            source: "sub-upsell",
          });
        }

        // Add/remove the free Derma Roller gift for the new subscription
        // (e.g. Peptide 1-month earns it) using the theme's own matrix.
        if (typeof itemsEl.reconcileSubscriptionGiftsDebounced === "function") {
          itemsEl.reconcileSubscriptionGiftsDebounced();
        }
        // The re-rendered widget is gone (line is now a subscription), so the
        // loading class disappears with it — nothing to reset here.
      })
      .catch(function (e) {
        console.error("[sub-upsell] swap error:", e);
        button.classList.remove(IN_FLIGHT);
        button.removeAttribute("aria-disabled");
      });
  }

  document.addEventListener("click", function (event) {
    var button = event.target.closest(".js-sub-upsell-switch");
    if (!button) return;
    event.preventDefault();
    swap(button);
  });
})();
